<template>
  <section class="guided-mode__card">
    <GuidedModeVisual
      :media="media"
      :fallback-icon="mdiHeartPulse"
      :size="mediaSize"
      :interactive="mediaInteractive"
      @toggle-size="$emit('toggle-size')"
    />
    <p class="guided-mode__prompt">{{ prompt }}</p>
    <p v-if="instructions" class="guided-mode__instructions">{{ instructions }}</p>
    <blood-pressure-input
      :value="inputValue"
      input-id="guided-blood-pressure"
      :label="prompt"
      :ranges="ranges"
      :unit="displayUnit"
      :systolic-label="$t('questions.blood_pressure.input_label.systolic')"
      :diastolic-label="$t('questions.blood_pressure.input_label.diastolic')"
      :systolic-invalid="systolicInvalid"
      :diastolic-invalid="diastolicInvalid"
      :systolic-warning="systolicWarning"
      :diastolic-warning="diastolicWarning"
      @parts="onPartsChanged"
      @entry-complete="$emit('entry-complete')"
    />
    <p
      class="guided-mode-blood-pressure__error"
      :class="{ 'guided-mode-blood-pressure__error--warning': validationSeverity === 'warning' }"
      role="alert"
      :aria-hidden="validationMessageToken ? 'false' : 'true'"
    >
      {{ validationMessageToken ? $t(validationMessageToken) : '' }}
    </p>
    <button v-if="allowUnknown" type="button" class="guided-mode__unknown"
            :class="{ 'guided-mode__unknown--selected': unknown }" :aria-pressed="String(unknown)"
            @click="$emit('unknown')">{{ $t('app.inputs.unknown.button') }}</button>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mdiHeartPulse } from '@mdi/js'
import { BloodPressureInput } from '@/components/assessment-inputs'
import GuidedModeVisual from './GuidedModeVisual.vue'
import type {
  GuidedBloodPressureRange,
  GuidedModeMedia,
  GuidedValidationSeverity,
} from '../types/guided-mode.types'
import type { BloodPressurePart } from '../utils/blood-pressure'

interface BloodPressureParts {
  systolic: string
  diastolic: string
}

export default defineComponent({
  name: 'GuidedModeBloodPressure',
  components: { BloodPressureInput, GuidedModeVisual },
  props: {
    prompt: { type: String, required: true },
    instructions: { type: String, default: '' },
    systolic: { type: String, default: '' },
    diastolic: { type: String, default: '' },
    systolicInvalid: { type: Boolean, default: false },
    diastolicInvalid: { type: Boolean, default: false },
    systolicWarning: { type: Boolean, default: false },
    diastolicWarning: { type: Boolean, default: false },
    ranges: { type: Object as PropType<GuidedBloodPressureRange | null>, default: null },
    displayUnit: { type: String, default: 'mmHg' },
    validationSeverity: {
      type: String as PropType<GuidedValidationSeverity | null>,
      default: null,
    },
    validationMessageToken: { type: String, default: '' },
    allowUnknown: { type: Boolean, default: false },
    unknown: { type: Boolean, default: false },
    media: { type: Object as PropType<GuidedModeMedia | null>, default: null },
    mediaSize: { type: String, default: 'default' },
    mediaInteractive: { type: Boolean, default: false },
  },
  emits: ['unknown', 'part-changed', 'toggle-size', 'entry-complete'],
  data: () => ({ mdiHeartPulse }),
  computed: {
    inputValue(): string {
      return this.systolic || this.diastolic ? `${this.systolic}/${this.diastolic}` : ''
    },
  },
  methods: {
    onPartsChanged(parts: BloodPressureParts): void {
      const changedParts: BloodPressurePart[] = ['systolic', 'diastolic']
      changedParts.forEach((part) => {
        if (parts[part] !== this[part]) this.$emit('part-changed', part, parts[part])
      })
    },
  },
})
</script>
